import React, { useState } from 'react';
import Icon from 'components/atoms/Icon';

interface FeedbackButtonsProps {
  classificationId: string;
  onFeedback: (classificationId: string, isPositive: boolean) => void;
  onThumbsDown?: () => void;
  initialFeedback?: 'up' | 'down' | null;
}

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  classificationId,
  onFeedback,
  onThumbsDown,
  initialFeedback = null,
}) => {
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(initialFeedback);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFeedback = async (isPositive: boolean) => {
    const newFeedback = isPositive ? 'up' : 'down';

    // If clicking the same button, do nothing
    if (feedback === newFeedback) {
      return;
    }

    // If thumbs down and onThumbsDown callback is provided, trigger reclassification flow
    if (!isPositive && onThumbsDown) {
      setFeedback(newFeedback);
      onThumbsDown();
      return;
    }

    setIsSubmitting(true);
    setFeedback(newFeedback);

    try {
      await onFeedback(classificationId, isPositive);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Revert on error
      setFeedback(feedback);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500">Was this helpful?</span>
      <button
        onClick={() => handleFeedback(true)}
        disabled={isSubmitting}
        className={`p-1.5 rounded transition-colors ${
          feedback === 'up'
            ? 'text-green-600 bg-green-50'
            : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        aria-label="Thumbs up"
      >
        <Icon name="thumbs-up" size={16} />
      </button>
      <button
        onClick={() => handleFeedback(false)}
        disabled={isSubmitting}
        className={`p-1.5 rounded transition-colors ${
          feedback === 'down'
            ? 'text-red-600 bg-red-50'
            : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        aria-label="Thumbs down"
      >
        <Icon name="thumbs-down" size={16} />
      </button>
    </div>
  );
};

export default FeedbackButtons;
